import { type Location, createBrowserHistory } from 'history';
import { unobserve, observe, type ApplicationController } from '@aha-app/mvc';
import makeDebugger from 'javascripts/util/devLog';

const debug = makeDebugger('framework:routing');

type RoutingProps = {
  router: ApplicationRouter;
  routes: (builder: RouteBuilder) => void;
  stateToPath: (builder: PathBuilder) => void;
};

type RoutableApplicationController = ApplicationController & RoutingProps;

interface Route {
  path: string;
  matcher: RegExp;
  paramNames: string[];
  callback: (params: any) => void;
}

// Remove leading or trailing slashes from a path segment.
function cleanSegment(segment: string) {
  return segment.replace(/^\//, '').replace(/\/$/, '');
}

// Helper class for building paths segment by segment.
class PathBuilder {
  private readonly _segments: string[] = [];
  private _hash: string = null;
  private _query: string = null;

  segment(str: string) {
    if (!str?.length) {
      throw new Error(
        `Added empty segment to path in stateToPath after '${this.path}'`
      );
    }
    this._segments.push(cleanSegment(str)); // using the external function
  }

  hash(str: string) {
    this._hash = str.replace(/^#/, '');
  }

  queryString(query: string) {
    this._query = query;
  }

  get path() {
    return (
      '/' +
      this._segments.join('/') +
      (this._hash ? '#' + this._hash : '') +
      (this._query ? '?' + this._query : '')
    );
  }

  get changed() {
    return this._segments.length !== 0 || this._hash !== null;
  }

  differsFromLocation(location: Location) {
    const locPath = location.pathname || '';
    const locHash = location.hash ? location.hash.replace('#', '') : null;
    return this.path !== locPath || this._hash !== locHash;
  }
}

export class RouteBuilder {
  prefix = [];
  routeList = [];

  // Add a namespace.
  namespace(path: string, subRoutes) {
    this.prefix.push(path);
    subRoutes();
    this.prefix.pop();
  }

  // Add a route.
  add(path: string, callback) {
    this.routeList.push({
      prefix: [...this.prefix],
      path,
      callback,
    });
  }

  // Generate and return the routing table.
  generate(): Route[] {
    const routesTable = [];
    for (const route of this.routeList) {
      const path = this.combineSegments([...route.prefix, route.path]);
      const [matcher, paramNames] = this.compilePath(path);

      routesTable.push({
        path,
        matcher,
        paramNames,
        callback: route.callback,
      });
    }
    return routesTable;
  }

  combineSegments(segments) {
    let path = '';
    for (const segment of segments) {
      let cleanedSegment = cleanSegment(segment);

      if (cleanedSegment !== '') {
        path += '/' + cleanedSegment;
      }
    }

    // Verify that * only occurs at end, and only occurs once.
    if (path.match(/\*.*.$/)) {
      throw new Error(
        `'*' can only appear at the end of a route path for '${path}'`
      );
    }
    if (path.match(/\*/g)?.length > 1) {
      throw new Error(`'*' can only appear in a route path once for '${path}'`);
    }

    return path;
  }

  compilePath(path: string): [RegExp, string[]] {
    let regexSource = '^';
    const paramNames = [];

    // Compile parameters.
    regexSource += path
      .replace(/\*/, '') // Remove trailing *
      .replace(/[\\.*+^$?{}|()[\]]/g, '\\$&') // Escape special regex chars
      .replace(/:(\w+)/g, (_, paramName) => {
        paramNames.push(paramName);
        return '([^\\/]+)';
      });

    // Handle a trailing *
    if (path.endsWith('*')) {
      paramNames.push('wildcard');
      regexSource += '(.*)$';
    } else {
      regexSource += '$';
    }

    return [new RegExp(regexSource, 'i'), paramNames];
  }
}

class ApplicationRouter {
  // The routing table. Routes are listed in priority order. Each route is:
  //
  //   {
  //     path,
  //     matcher,
  //     paramNames,
  //     callback,
  //   }
  routes: Route[] = [];

  // History library instance for wrapping the browser's history and location APIs.
  history: ReturnType<typeof createBrowserHistory> = null;
  historyUnlisten = null;
  controller: RoutableApplicationController;
  observer: any;

  constructor(controller: RoutableApplicationController) {
    this.controller = controller;
    const builder = new RouteBuilder();
    controller.routes(builder);
    this.routes = builder.generate();
    // This is useful when debugging:
    // this.logRoutes();

    this.resetHistory();

    // This function is reactive and will execute automatically any time
    // the controller state that it depends upon is modified.
    const observerFunction = () => {
      const builder = new PathBuilder();
      this.controller.stateToPath(builder);

      if (
        builder.changed &&
        builder.differsFromLocation(this.history.location)
      ) {
        debug(`Router setting location to ${builder.path}`);
        this.history.push(builder.path);
      }
    };
    if (this.controller.stateToPath) {
      this.observer = observe(observerFunction, { lazy: true });
    }
  }

  resetHistory() {
    this.history = createBrowserHistory();
  }

  destroy() {
    if (this.historyUnlisten) {
      this.historyUnlisten();
    }
    if (this.observer) {
      unobserve(this.observer);
    }
  }

  // Bring the application in sync with the browser by navigating to the
  // current path.
  navigateSync() {
    this.navigate(this.history.location.pathname);
    this.observer();

    if (!this.historyUnlisten) {
      // Listen for changes to the current location.
      this.historyUnlisten = this.history.listen((location, action) => {
        if (action === 'POP') {
          this.navigate(location.pathname);
        }
      });
    }
  }

  match(path: string) {
    for (const route of this.routes) {
      const matches = route.matcher.exec(path);
      if (!matches) continue;

      if (matches.length - 1 !== route.paramNames.length) {
        throw new Error(
          `Route returned ${matches.length - 1} matches, but expected ${
            route.paramNames.length
          } for ${route.path}`
        );
      }

      const args = {};
      matches
        .slice(1)
        .forEach((match, index) => (args[route.paramNames[index]] = match));
      return { route, args };
    }
  }

  hasMatch(path: string) {
    return !!this.match(path);
  }

  // Navigate to a path.
  navigate(path: string) {
    // Find a matching route.
    const match = this.match(path);
    if (!match) {
      console.error(`Could not match route for ${path}`);
      return;
    }

    // Found a match.
    debug(`Routing: ${path} => ${match.route.path}`);

    match.route.callback({ controller: this.controller, ...match.args });
  }

  logRoutes() {
    for (const route of this.routes) {
      console.log(`${route.path}`);
    }
  }
}

export interface IRoutingBehaviors {
  // NOTE the presence of this is used to check whether we're in a client-side routing
  // context in some cases - e.g. see WhiteboardController.clientSideRouting
  router: ApplicationRouter;
}

// Mixin for ApplicationController so that it can synchronize state with the
// browser location.
//
// Use like:
//
//     class MyController extends RoutingBehaviors(ApplicationController) {
//
// or
//
//    class MyController extends ApplicationController {}
//    MyController = RoutingBehaviors(MyController);
//
function RoutingBehaviors<
  T extends { new (...args: any[]): ApplicationController }
>(originalClass: T) {
  return class extends originalClass {
    router: ApplicationRouter;

    constructor(...args: any[]) {
      super(...args);

      this.router = new ApplicationRouter(this as any);
    }

    internalInitialize(
      ...args: Parameters<ApplicationController['internalInitialize']>
    ) {
      const initializeRouter = !this.initialized;
      super.internalInitialize(...args);

      if (initializeRouter) {
        this.router.navigateSync();
      }
    }

    internalDestroy() {
      super.internalDestroy();
      this.router.destroy();
    }
  };
}

export { PathBuilder };
export default RoutingBehaviors;
